type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  url: string;
  userAgent: string;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private isDev = import.meta.env.DEV;

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private createLogEntry(level: LogLevel, message: string, data?: any): LogEntry {
    return {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
  }

  private storeLog(entry: LogEntry) {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  debug(message: string, data?: any) {
    const entry = this.createLogEntry('debug', message, data);
    this.storeLog(entry);
    
    if (this.isDev) {
      console.debug(`[${entry.timestamp}] ${message}`, data);
    }
  }

  info(message: string, data?: any) {
    const entry = this.createLogEntry('info', message, data);
    this.storeLog(entry);
    
    console.info(`[${entry.timestamp}] ${message}`, data);
  }

  warn(message: string, data?: any) {
    const entry = this.createLogEntry('warn', message, data);
    this.storeLog(entry);
    
    console.warn(`[${entry.timestamp}] ${message}`, data);
  }

  error(message: string, error?: Error | any) {
    const entry = this.createLogEntry('error', message, {
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack,
      } : error,
    });
    this.storeLog(entry);
    
    console.error(`[${entry.timestamp}] ${message}`, error);
  }

  // Get recent logs for debugging
  getRecentLogs(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  // Clear logs
  clear() {
    this.logs = [];
  }

  // Export logs as JSON (useful for debugging)
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Send logs to server (for production error reporting)
  async flushLogs() {
    const logsToSend = this.logs.filter(log => log.level === 'error' || log.level === 'warn');
    
    if (logsToSend.length === 0) return;

    try {
      // In production, you could send these to your backend
      if (!this.isDev) {
        console.log('[Logger] Would send logs to server:', logsToSend.length);
      }
    } catch (error) {
      console.error('[Logger] Failed to flush logs:', error);
    }
  }
}

export const logger = Logger.getInstance();

// Auto-flush logs periodically in production
if (!import.meta.env.DEV) {
  setInterval(() => {
    logger.flushLogs();
  }, 60000); // Every minute
}
